import {useMemo} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import ChekinProSettings from 'ChekinProSettings';
import {SDK_MODE} from 'utils/embedded';
import {SHOWED_GUEST_GROUP_MODAL, IS_LEADER_GUEST_STORAGE_KEY} from 'utils/constants';
import {PATHS} from 'Routes';
import {SessionStorage} from '@guestapp/core';
import {device} from '@guestapp/ui/src/utils/device';
import {useIVStore} from 'store/identityVerification';
import {useShareButtonStore} from 'components/ShareButton';
import {useGuestGroup, useModalOncePerSession, usePath, useScreenResize} from 'hooks';
import {useSchema} from 'context/guest';
import {useReservation} from 'context/reservation';
import {useSummary} from 'context/summary';
import {useReservationSettingsModal} from 'components/ReservationSettingsModal';
import Page from '../../components/Page';
import GuestsPanel from 'components/GuestsPanel';
import {IncompleteGuestsModal} from './components/IncompleteGuestsModal';
import {HeaderGuestInformation} from './components/HeaderGuestInformation';
import {InformationText, ModalTitle, GuestsList} from './components';
import {PmsGuestPanelContainer, Wrapper} from './styled';

function GuestRegistrationView() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {data: reservation, hasAvantio} = useReservation();
  const {isGuestRegistrationOnly, isInstantCheckInEnabled} = useSummary();
  const {guestGroup, isLoading} = useGuestGroup();
  const {getFullPath} = usePath();
  const {resetAllIVState} = useIVStore();
  const {isMatch: isMobile} = useScreenResize(device.tabletSubHeader);
  const location = useLocation();

  const openShareLinkModal = useShareButtonStore(state => state.openModal);

  const {
    identityVerificationDetails: {enabled: ivEnabled},
  } = useSchema();
  const {isBiometricMatchForAll} = useReservation();

  const {shouldOpenModal} = useModalOncePerSession({
    id: reservation?.id,
    storageKey: SHOWED_GUEST_GROUP_MODAL,
  });

  const {
    Modal: WhoIsComingModal,
    openModal,
    ageLimit,
  } = useReservationSettingsModal({
    openModalPerSession: shouldOpenModal,
    showFirstTime: isGuestRegistrationOnly,
  });

  const hasNoGuests = guestGroup?.number_of_fully_registered_guests === 0;
  const totalGuest = guestGroup?.known_number_of_guests || 5;
  const currentTotalGuest = guestGroup?.number_of_fully_registered_guests || 0;

  const nextPage = useMemo(() => {
    const goToIv = ivEnabled && (hasNoGuests || isBiometricMatchForAll);

    const goToForm = !ivEnabled || (ivEnabled && !isBiometricMatchForAll && !hasNoGuests);

    if (goToIv) {
      return getFullPath(PATHS.verification.main);
    }
    if (goToForm) {
      return getFullPath(PATHS.addPersonalDataForm);
    }
    return getFullPath(PATHS.verification.main);
  }, [ivEnabled, getFullPath, isBiometricMatchForAll, hasNoGuests]);

  const onBackClick = () => {
    navigate(getFullPath(PATHS.home), {replace: true});
  };

  const handleRegisterClick = () => {
    resetAllIVState();
    navigate(nextPage);
  };

  const handleRegisterNewGuest = (isLeader?: boolean) => {
    SessionStorage.set(IS_LEADER_GUEST_STORAGE_KEY, isLeader);
    handleRegisterClick();
  };

  const handleCompleteGuestData = () => {
    navigate(location.pathname, {replace: true, state: null});
  };

  return (
    <Page
      title={t('guest_summary')}
      isLoading={isLoading}
      onBackButtonClick={onBackClick}
      navBarTitle={isMobile ? t('guest_summary') : t('online_chekin')}
      className="guest-register-view"
      isSubHeaderVisible={ChekinProSettings.mode !== SDK_MODE.guestRegistrationOnly}
      isBackButtonDisabled={ChekinProSettings.mode === SDK_MODE.guestRegistrationOnly}
    >
      <Wrapper className="guest-register-view__content">
        {isInstantCheckInEnabled && (
          <HeaderGuestInformation
            currentGuests={currentTotalGuest}
            totalGuests={totalGuest}
            onEditClick={openModal}
          />
        )}
        {!isInstantCheckInEnabled && (
          <ModalTitle
            currentTotalGuest={currentTotalGuest}
            totalGuest={totalGuest}
            onEditClick={openModal}
          />
        )}
        {Boolean(ageLimit) && <InformationText ageLimit={ageLimit} />}
        {hasAvantio || isInstantCheckInEnabled ? (
          <PmsGuestPanelContainer className="guest-register-view__guest-panel">
            <GuestsPanel handleRegisterNewGuest={handleRegisterNewGuest} />
          </PmsGuestPanelContainer>
        ) : (
          <GuestsList
            nextPage={nextPage}
            handleRegisterNewGuest={handleRegisterNewGuest}
          />
        )}
      </Wrapper>
      {WhoIsComingModal}

      <IncompleteGuestsModal
        onShareRegistrationLink={openShareLinkModal}
        onCompleteGuestData={handleCompleteGuestData}
      />
    </Page>
  );
}

export {GuestRegistrationView};
